// Pure, dependency-free review checks for the Sales forecast spine (Phase 1).
// No network, no React, no imports — just functions over plain values, so they
// are trivially testable and safe to run on every render.
//
// Two layered concepts:
//   • the base STATE (provenance) of a closed-month actual, derived from the row
//   • a CHECK verdict (pass / warn / fail) laid on top of the value
//
// Invariants the caller must uphold (see SalesForecast.jsx):
//   • actual_units always wins over actual_ingested (display AND forecast basis)
//   • an unreviewed actual_ingested NEVER drives the forecast cascade
//
// These functions are intentionally tolerant of nulls and bad input — they only
// ever return a verdict, never throw.

export const VARIANCE_WARN = 0.5;   // |value − prior-mean| / prior-mean > 50% → warn
export const VARIANCE_LOOKBACK = 3; // months of prior actuals to average

// Base provenance state for a closed-month actual.
//   actual_units  : approved/effective value (number) or null
//   actual_ingested : staged Shopify value (number) or null
//   actual_source : 'manual' | 'shopify_athena' | 'derived_weekly' | null
// Returns one of: 'approved_manual' | 'approved_shopify' | 'ingested' | 'missing'
export function actualState(actual_units, actual_ingested, actual_source) {
  if (actual_units != null) {
    return actual_source === 'shopify_athena' ? 'approved_shopify' : 'approved_manual';
  }
  if (actual_ingested != null) return 'ingested'; // staged, needs review
  return 'missing';
}

// Best-effort SKU classification ("where detectable") from the catalog row.
// Drives the bundle/accessory/subscription warning. Returns:
//   'product' | 'accessory' | 'subscription' | 'kit'
export function classifySku(catalogRow) {
  if (!catalogRow) return 'product';
  const code = String(catalogRow.sku_code || '').toUpperCase();
  const name = String(catalogRow.sku_name || '').toLowerCase();
  const cat = String(catalogRow.category || '').toLowerCase();
  if (/subscription|love pack/.test(name) || code.startsWith('OL-LP')) return 'subscription';
  if (/\bkit\b/.test(name)) return 'kit';
  if (cat === 'other' || code.startsWith('FRTHR') || code.startsWith('BTL')) return 'accessory';
  return 'product';
}

const KIND_WARN = {
  subscription: 'Subscription/bundle — units may attribute to component SKUs; verify',
  kit: 'Kit component — may not sell standalone on Shopify; verify',
  accessory: 'Accessory — may have no (or a different) Shopify sales SKU; verify',
};

// checkActual — verdict for ONE closed-month actual value.
//   value        : effective actual being reviewed (number) or null
//   isClosed     : only closed months carry an "actual" to check
//   priorActuals : chronological prior closed-month effective actuals (numbers)
//   skuKind      : 'product' | 'accessory' | 'subscription' | 'kit'
// Returns { level: 'pass' | 'warn' | 'fail', reasons: string[] }.
export function checkActual({ value, isClosed, priorActuals = [], skuKind = 'product' } = {}) {
  const reasons = [];
  let level = 'pass';
  const bump = (l) => {
    if (l === 'fail') level = 'fail';
    else if (l === 'warn' && level !== 'fail') level = 'warn';
  };

  // gate: checks only apply to a closed month that actually has a value
  if (!isClosed || value == null) return { level, reasons };
  const v = Number(value);
  if (!Number.isFinite(v)) return { level, reasons };

  // negative / zero / refund anomaly
  if (v < 0) { reasons.push('Negative units — refunds exceeded sales'); bump('fail'); }
  else if (v === 0) { reasons.push('Zero units recorded'); bump('warn'); }

  // variance vs prior actuals (needs ≥2 prior points and a non-zero base)
  const recent = priorActuals.filter((n) => Number.isFinite(n)).slice(-VARIANCE_LOOKBACK);
  if (recent.length >= 2 && v >= 0) {
    const mean = recent.reduce((s, n) => s + n, 0) / recent.length;
    if (mean > 0) {
      const dev = Math.abs(v - mean) / mean;
      if (dev > VARIANCE_WARN) {
        const pct = Math.round(((v - mean) / mean) * 100);
        reasons.push(`${pct >= 0 ? '+' : ''}${pct}% vs prior ${recent.length}-mo avg (${Math.round(mean).toLocaleString()})`);
        bump('warn');
      }
    }
  }

  // bundle / accessory / subscription
  if (KIND_WARN[skuKind]) { reasons.push(KIND_WARN[skuKind]); bump('warn'); }

  return { level, reasons };
}
